import { z } from 'zod'
import { resolveEffectiveMinUsd } from './rewards-config'

export function normalizeRedeemAmountInput(raw: string): number | null {
  const trimmed = raw.trim()
  if (!trimmed || trimmed.length > 12) return null

  let dotCount = 0
  for (const ch of trimmed) {
    if (ch === '.') {
      dotCount += 1
      if (dotCount > 1) return null
    } else if (ch < '0' || ch > '9') {
      return null
    }
  }

  const parsed = Number(trimmed)
  if (!Number.isFinite(parsed)) return null
  return Math.round(parsed * 100) / 100
}

export function createRedeemAmountSchema(
  maxBalanceUsd: number,
  minAmountUsd: number,
  brandMaxAmountUsd: number = maxBalanceUsd,
  allowedAmountsUsd?: number[],
) {
  const effectiveMinUsd = resolveEffectiveMinUsd(minAmountUsd)
  const effectiveMaxUsd = Math.min(maxBalanceUsd, brandMaxAmountUsd)

  return z.object({
    amountInput: z
      .string()
      .min(1, 'Please enter a valid amount')
      .superRefine((val, ctx) => {
        const normalized = normalizeRedeemAmountInput(val)
        if (normalized === null) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Please enter a valid amount' })
          return
        }
        if (allowedAmountsUsd?.length) {
          if (!allowedAmountsUsd.includes(normalized)) {
            const amounts = allowedAmountsUsd.map((a) => `$${a.toFixed(2)}`).join(', ')
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Choose one of these amounts: ${amounts}`,
            })
          }
          return
        }
        if (normalized < effectiveMinUsd) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Minimum redemption amount is $${effectiveMinUsd.toFixed(2)}`,
          })
        }
        if (normalized > effectiveMaxUsd) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              effectiveMaxUsd < maxBalanceUsd
                ? `Maximum redemption amount for this brand is $${effectiveMaxUsd.toFixed(2)}`
                : `Amount exceeds your available balance of $${maxBalanceUsd.toFixed(2)}`,
          })
        }
      }),
  })
}

export type RedeemAmountFormData = z.infer<ReturnType<typeof createRedeemAmountSchema>>
